// src/components/Shopping/OfferProductCard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";

export default function OfferProductCard({ product }) {
  // Hook to programmatically navigate
  const navigate = useNavigate();

  // Handle button click to redirect to ProductDetail page
  const handleGrabOffer = () => {
    // console.log(`Navigating to product details for ${product.name}`);
    navigate(`/product/${product.id}`);
  };

  // Ensure we have valid numeric values with fallbacks
  const discountedPrice = product.discountedPrice || 0;
  const originalPrice = product.originalPrice || 0;
  const discount = product.discount || 0;
  const offerType = product.offerType || "Discount";
  
  // Handle missing image
  const productImage = product.image || "/api/placeholder/400/320";

  // Determine if there's an actual price difference
  const hasDiscount = originalPrice > discountedPrice && discount > 0;

  return (
    <div className="relative w-72 h-[30rem] bg-white rounded-sm shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-shadow duration-300 mb-7">
      {/* Offer Ribbon */}
      <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden z-10">
        <div className="absolute top-0 right-0 bg-[#D4AF37] text-black text-xs font-bold transform rotate-45 translate-x-8 translate-y-2 px-8 py-1">
          {offerType === "Discount" ? "Special Discount" : "Special Offer"}
        </div>
      </div>

      {/* Image Container */}
      <div className="relative group">
        <img
          src={productImage}
          alt={product.name || "Product Image"}
          className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-110"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/api/placeholder/400/320";
          }}
        />
        {/* Discount Badge - Only show if there's actual discount */}
        {hasDiscount && (
          <span className="absolute bottom-4 left-4 bg-black text-white text-sm font-bold px-3 py-1 rounded-full">
            {discount}% OFF
          </span>
        )}
        {/* Special Offer Badge - Show for offer type without discount */}
        {offerType === "Offer" && !hasDiscount && (
          <span className="absolute bottom-4 left-4 bg-[#D4AF37] text-black text-sm font-bold px-3 py-1 rounded-full">
            SPECIAL OFFER
          </span>
        )}
      </div>

      {/* Content Container */}
      <div className="p-5 flex flex-col h-[12rem] justify-between bg-gradient-to-t from-gray-50 to-white">
        {/* Product Name */}
        <h3
          className="text-xl font-semibold text-[#333333] truncate"
          title={product.name || "Product Name"}
        >
          {product.name || "Product Name"}
        </h3>

        {/* Price Section */}
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-[#000000]">
            ₹{discountedPrice.toLocaleString()}
          </span>
          {hasDiscount && (
            <span className="text-gray-400 line-through text-sm">
              ₹{originalPrice.toLocaleString()}
            </span>
          )}
        </div>

        {/* Timer/Offer Info */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="text-[#D4AF37]">⏳</span>
          <span>
            {offerType === "Discount" 
              ? "Limited Discount: Ends Soon!" 
              : "Exclusive Offer: Don't Miss Out!"
            }
          </span>
        </div>

        {/* Button with Redirect */}
        <button
          onClick={handleGrabOffer}
          className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black py-2 px-4 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-200"
        >
          {offerType === "Discount" ? "Grab Discount Now" : "View Offer"}
        </button>
      </div>
    </div>
  );
}